import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import {
  getTimelineEvents,
  createTimelineEvent,
  updateTimelineEvent,
  deleteTimelineEvent,
  deleteAllTimelineEvents,
} from '../../services/timelineService'
import { getSuppliers } from '../../services/supplierService'
import InfoButton from '../../components/InfoButton/InfoButton'
import Loader from '../../components/Loader/Loader'
import TextareaWithCounter from '../../components/TextareaWithCounter/TextareaWithCounter'
import { showAlert, showConfirm } from '../../services/dialogService'
import './Section.css'
import './TimelineCreator.css'

const TEMPLATES = [
  {
    id: 'ceremony-cocktail',
    name: 'Ceremony + Cocktail Wedding',
    timeRange: '4:00 PM – 11:00 PM',
    events: [
      { event_time: '16:00', event_name: 'Guests arrive + welcome drinks' },
      { event_time: '16:30', event_name: 'Ceremony – cue the romance and waterproof mascara' },
      { event_time: '17:00', event_name: 'Ceremony ends – "Did anyone else cry or just me?"' },
      { event_time: '17:05', event_name: 'Cocktail hour (5:00 – 6:30 PM) – and strategic stalking of the grazing table' },
      { event_time: '18:30', event_name: 'Couple grand entrance – lights, camera, couple' },
      { event_time: '18:35', event_name: 'Quick welcome toast – short, sweet, and no dad jokes (yet)' },
      { event_time: '18:45', event_name: 'Cocktail-style dinner begins' },
      { event_time: '19:00', event_name: 'Speech #1: One bestie brings the love, the laughs, and possibly a printed Google Doc' },
      { event_time: '19:30', event_name: 'Speech #2: Sibling or chosen fam – a little spicy, a little emotional' },
      { event_time: '20:15', event_name: 'Cake cutting – cue the sugar rush and phones out' },
      { event_time: '20:30', event_name: 'First dance – this is your movie scene' },
      { event_time: '20:35', event_name: 'Parent dances – tissues: deployed' },
      { event_time: '20:45', event_name: 'Dance floor opens – your moment to unleash your inner Beyoncé' },
      { event_time: '22:00', event_name: 'Late-night snacks – because tiny tacos > inner peace' },
      { event_time: '23:00', event_name: 'Last song – ABBA, Queen, or chaos. Your call.' },
    ],
  },
  {
    id: 'ceremony-seated',
    name: 'Ceremony + Seated Reception',
    timeRange: '4:00 PM – 11:00 PM',
    events: [
      { event_time: '16:00', event_name: 'Guests arrive, sip something bubbly, and ask where the bathroom is' },
      { event_time: '16:30', event_name: 'Ceremony – vows, sniffles, someone\'s cousin crying louder than the couple' },
      { event_time: '17:00', event_name: 'Ceremony ends – guests cheer, people wipe tears, and someone starts aggressively looking for the bar' },
      { event_time: '17:05', event_name: 'Cocktail hour (5:00 – 6:00 PM) – bites, sips, and someone already slightly drunk' },
      { event_time: '18:15', event_name: 'Guests seated' },
      { event_time: '18:30', event_name: 'Couple grand entrance – one half doing jazz hands, the other pretending to be cool' },
      { event_time: '18:35', event_name: 'Welcome speech – likely a parent or close friend who practiced for weeks' },
      { event_time: '18:45', event_name: 'Entrée served' },
      { event_time: '19:15', event_name: 'Speech #1: Maid of honor / Best person – tears incoming' },
      { event_time: '19:30', event_name: 'Main course served' },
      { event_time: '20:00', event_name: 'Speech #2: A wild card (sibling, cousin, funny friend) – heartfelt chaos' },
      { event_time: '20:15', event_name: 'Speech #3: A parent or mentor – wise, warm, a little long but forgiven' },
      { event_time: '20:30', event_name: 'Dessert served' },
      { event_time: '20:50', event_name: 'Cake cutting – cute moment, frosting everywhere' },
      { event_time: '21:00', event_name: 'First dance – literal main character energy' },
      { event_time: '21:05', event_name: 'Parent dances – someone\'s lip-syncing the lyrics, for sure' },
      { event_time: '21:10', event_name: 'Dance floor opens – this is where the real story begins' },
      { event_time: '22:15', event_name: 'Late-night snacks – bless whoever thought of this' },
      { event_time: '23:00', event_name: 'Last song + hug everyone + vanish mysteriously' },
    ],
  },
  {
    id: 'seated-formal',
    name: 'Seated Reception Only — Formal Dinner',
    timeRange: '6:00 PM – 11:00 PM',
    events: [
      { event_time: '18:00', event_name: 'Guests arrive, already scouting the dessert table' },
      { event_time: '18:30', event_name: 'Couple grand entrance – applause, maybe some light twerking' },
      { event_time: '18:35', event_name: 'Quick welcome toast' },
      { event_time: '18:45', event_name: 'Guests seated' },
      { event_time: '19:00', event_name: 'Entrée served' },
      { event_time: '19:30', event_name: 'Speech #1: Friend who\'s known the couple since "before they were a thing"' },
      { event_time: '19:45', event_name: 'Main course served' },
      { event_time: '20:15', event_name: 'Speech #2: Parent or honorary aunt – brings wisdom and a killer outfit' },
      { event_time: '20:30', event_name: 'Speech #3: Best friend with a mic and zero chill – bring tissues' },
      { event_time: '20:45', event_name: 'Dessert served' },
      { event_time: '21:00', event_name: 'Cake cutting – small moment, big applause' },
      { event_time: '21:10', event_name: 'First dance – everyone\'s filming it like it\'s Coachella' },
      { event_time: '21:15', event_name: 'Parent dances – wholesome, sweet, slightly off beat' },
      { event_time: '21:20', event_name: 'Dance floor opens – now it\'s a party' },
      { event_time: '22:15', event_name: 'Late-night snacks appear like magic' },
      { event_time: '23:00', event_name: 'Final song – you made it, legends' },
    ],
  },
  {
    id: 'seated-food-stations',
    name: 'Seated Reception Only — Food Stations',
    timeRange: '6:00 PM – 11:00 PM',
    events: [
      { event_time: '18:00', event_name: 'Guests arrive – and go straight to the bar, obviously' },
      { event_time: '18:30', event_name: 'Couple grand entrance – strutting like a Vogue runway' },
      { event_time: '18:45', event_name: 'Welcome speech – short and sassy' },
      { event_time: '19:00', event_name: 'Food stations open' },
      { event_time: '19:15', event_name: 'Speech #1: Sibling or bestie – equal parts roasts and love' },
      { event_time: '19:45', event_name: 'Speech #2: Parent or family rep – heartfelt, 20% too long, but we forgive' },
      { event_time: '20:30', event_name: 'Cake cutting – polite applause and sugar highs' },
      { event_time: '20:40', event_name: 'First dance – everyone filming like they\'re gonna submit it for an award' },
      { event_time: '20:45', event_name: 'Parent dances – cue the collective "awww"' },
      { event_time: '20:50', event_name: 'Dance floor opens – DJ drops that beat, heels come off' },
      { event_time: '22:15', event_name: 'Late-night snack drop – MVP move' },
      { event_time: '23:00', event_name: 'Last dance – then disappear like mysterious icons' },
    ],
  },
  {
    id: 'build-it-your-way',
    name: 'Build It Your Way',
    timeRange: 'Start from scratch — add your own events',
    events: [],
  },
]

function formatTimeForDisplay(timeStr) {
  if (!timeStr) return '—'
  const [h, m] = (timeStr + '').split(':')
  const hour = parseInt(h, 10)
  const ampm = hour >= 12 ? 'pm' : 'am'
  const h12 = hour % 12 || 12
  return `${h12}:${(m || '00').padStart(2, '0')} ${ampm}`
}

function formatTimeForInput(timeStr) {
  if (!timeStr) return ''
  const s = (timeStr + '').substring(0, 5)
  return s.length >= 5 ? s : ''
}

function TimelineCreator() {
  const { user, loading: authLoading } = useAuth()
  const [events, setEvents] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)

  const [form, setForm] = useState({
    event_name: '',
    event_time: '',
    vendor: '',
    notes: '',
  })

  useEffect(() => {
    if (!user) return
    const load = async () => {
      try {
        setLoading(true)
        const [evts, supps] = await Promise.all([
          getTimelineEvents(user.id),
          getSuppliers(user.id, true),
        ])
        setEvents(evts)
        setSuppliers(supps)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [user])

  const resetForm = () => {
    setForm({ event_name: '', event_time: '', vendor: '', notes: '' })
    setEditingEvent(null)
  }

  const openAddModal = () => {
    resetForm()
    setShowAddModal(true)
  }

  const openEditModal = (event) => {
    setEditingEvent(event)
    setForm({
      event_name: event.event_name || '',
      event_time: formatTimeForInput(event.event_time),
      vendor: event.supplier_id || '',
      notes: event.notes || '',
    })
    setShowAddModal(true)
  }

  const handleSave = async (addMore = false) => {
    if (!form.event_name.trim()) return
    try {
      const payload = {
        event_name: form.event_name.trim(),
        event_time: form.event_time || null,
        notes: form.notes.trim() || null,
        supplier_id: form.vendor || null,
      }
      if (editingEvent) {
        const updated = await updateTimelineEvent(editingEvent.id, payload)
        setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))
      } else {
        const created = await createTimelineEvent(user.id, payload)
        setEvents((prev) => [...prev, created].sort((a, b) => (a.event_time || '').localeCompare(b.event_time || '')))
      }
      if (addMore) {
        setForm({ ...form, event_name: '', event_time: '', notes: '' })
        setEditingEvent(null)
      } else {
        setShowAddModal(false)
        resetForm()
      }
    } catch (err) {
      console.error(err)
      await showAlert('Error saving')
    }
  }

  const handleDelete = async (event) => {
    const ok = await showConfirm('Delete this activity?', { title: 'Delete', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })
    if (!ok) return
    try {
      await deleteTimelineEvent(event.id)
      setEvents((prev) => prev.filter((e) => e.id !== event.id))
      if (editingEvent?.id === event.id) {
        setShowAddModal(false)
        resetForm()
      }
    } catch (err) {
      console.error(err)
      await showAlert('Error deleting')
    }
  }

  const toggleHighlight = async (event) => {
    try {
      const updated = await updateTimelineEvent(event.id, {
        highlighted: !event.highlighted,
      })
      setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))
    } catch (err) {
      console.error(err)
    }
  }

  const loadTemplate = async (template) => {
    const ok = await showConfirm(`Replace your timeline with the template "${template.name}"?`, { title: 'Load template', confirmLabel: 'Replace', cancelLabel: 'Cancel' })
    if (!ok) return
    try {
      setLoading(true)
      await deleteAllTimelineEvents(user.id)
      for (const ev of template.events) {
        await createTimelineEvent(user.id, {
          event_name: ev.event_name,
          event_time: ev.event_time,
        })
      }
      const fresh = await getTimelineEvents(user.id)
      setEvents(fresh)
      setSelectedTemplate(template)
      setShowTemplatePicker(false)
    } catch (err) {
      console.error(err)
      await showAlert('Error loading template')
    } finally {
      setLoading(false)
    }
  }

  const handleExport = () => {
    const lines = [
      'TIMELINE',
      selectedTemplate ? `${selectedTemplate.name} (${selectedTemplate.timeRange})` : '',
      '',
      'Time\tActivity',
      ...events.map((e) => {
        const act = e.event_name + (e.notes ? `\n${e.notes}` : '')
        return `${formatTimeForDisplay(e.event_time)}\t${act.replace(/\n/g, ' ')}`
      }),
    ]
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'timeline.txt'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const handlePrint = () => {
    window.print()
  }

  const getSupplierName = (supplierId) => {
    if (!supplierId) return null
    const s = suppliers.find((x) => x.id === supplierId)
    return s ? (s.name || s.business_name || s.category) : null
  }

  if (authLoading || loading) return <Loader />

  return (
    <div className="dashboard-section timeline-creator">
      <h1 className="timeline-creator-title">
        TIMELINE CREATOR
        <InfoButton
          title="Timeline Creator"
          content={
            <div>
              <p>
                Because who doesn&apos;t love a good plan? We&apos;ve added sample timelines to get you started —
                but feel free to make it your own. Add, remove, or shuffle rows until it actually looks like your day.
              </p>
              <p><strong>Features:</strong></p>
              <ul>
                <li>Choose from 4 timeline templates (click the title)</li>
                <li>Add, edit, and delete activities</li>
                <li>Link activities to your booked vendors</li>
                <li>Highlight items for planning or in the final timeline</li>
                <li>Export and print — highlights included</li>
              </ul>
            </div>
          }
        />
      </h1>

      <div className="timeline-creator-header">
        <button
          type="button"
          className="timeline-creator-title-btn"
          onClick={() => setShowTemplatePicker(true)}
          aria-expanded={showTemplatePicker}
          aria-haspopup="listbox"
        >
          {selectedTemplate
            ? `${selectedTemplate.name} (${selectedTemplate.timeRange})`
            : events.length
              ? 'Your timeline — click to choose template'
              : 'Choose a template to start'}
        </button>
        <div className="timeline-creator-actions">
          <button type="button" onClick={() => showAlert('Coming soon — this feature will be enabled in a future update.')}>
            Export
          </button>
          <button type="button" onClick={() => showAlert('Coming soon — this feature will be enabled in a future update.')}>
            Print
          </button>
          <button type="button" className="timeline-creator-highlight-hint" onClick={() => showAlert('Coming soon — this feature will be enabled in a future update.')}>
            Highlight
          </button>
          <button type="button" className="timeline-creator-add-btn" onClick={openAddModal}>
            Add activity
          </button>
        </div>
      </div>

      {showTemplatePicker && (
        <div className="timeline-template-picker" role="listbox">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              role="option"
              className="timeline-template-option"
              onClick={() => loadTemplate(t)}
            >
              {t.name} ({t.timeRange})
            </button>
          ))}
          <button
            type="button"
            className="timeline-template-close"
            onClick={() => setShowTemplatePicker(false)}
          >
            Cancel
          </button>
        </div>
      )}

      <div className="timeline-table-wrap">
        <table className="timeline-table">
          <thead>
            <tr>
              <th className="timeline-th-check" />
              <th>Time</th>
              <th>Activity</th>
              <th className="timeline-th-actions" />
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className={e.highlighted ? 'highlighted' : ''}>
                <td className="timeline-td-check">
                  <input
                    type="checkbox"
                    checked={!!e.highlighted}
                    onChange={() => toggleHighlight(e)}
                    aria-label="Highlight"
                  />
                </td>
                <td className="timeline-time">{formatTimeForDisplay(e.event_time)}</td>
                <td>
                  <div className="timeline-activity-main">{e.event_name}</div>
                  {e.notes && <div className="timeline-activity-notes">{e.notes}</div>}
                  {getSupplierName(e.supplier_id) && (
                    <div className="timeline-activity-vendor">Vendor: {getSupplierName(e.supplier_id)}</div>
                  )}
                </td>
                <td className="timeline-td-actions">
                  <div className="timeline-actions-wrap">
                    <button type="button" className="timeline-btn-edit" onClick={() => openEditModal(e)}>
                      Edit
                    </button>
                    <button type="button" className="timeline-btn-delete" onClick={() => handleDelete(e)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr>
                <td colSpan="4" className="timeline-empty">
                  No activities yet. Click &quot;Add activity&quot; or choose a template above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div
          className="timeline-add-modal"
          onClick={async (e) => {
            if (e.target !== e.currentTarget) return
            const ok = await showConfirm('Leave without saving? Your changes will not be saved.', { title: 'Discard changes?', confirmLabel: 'Leave', cancelLabel: 'Stay', variant: 'danger' })
            if (ok) {
              setShowAddModal(false)
              resetForm()
            }
          }}
        >
          <div className="timeline-add-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="timeline-add-modal-header">
              <h2>{editingEvent ? 'Edit activity' : 'Add activity item'}</h2>
              <button type="button" className="timeline-add-modal-close" onClick={() => { setShowAddModal(false); resetForm() }}>
                ✕ Cancel
              </button>
            </div>
            <div className="timeline-add-modal-body">
              <label>
                Activity
                <input
                  type="text"
                  value={form.event_name}
                  onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                  placeholder="e.g. Ceremony - vows and I dos"
                />
              </label>
              <label>
                Time
                <input
                  type="time"
                  value={form.event_time}
                  onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                />
              </label>
              <label>
                Vendor
                <select
                  value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                >
                  <option value="">None</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name || s.business_name || s.category || 'Vendor'}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Notes
                <TextareaWithCounter
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional notes (shown below activity)"
                  rows={3}
                  maxLength={500}
                />
              </label>
            </div>
            <div className="timeline-add-modal-footer">
              <button type="button" className="timeline-save-btn" onClick={() => handleSave(false)}>
                Save activity
              </button>
              <button type="button" className="timeline-save-add-btn" onClick={() => handleSave(true)}>
                Save and add more
              </button>
              {editingEvent && (
                <button type="button" className="timeline-delete-in-modal" onClick={() => handleDelete(editingEvent)}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TimelineCreator
